import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core'
import { CurrencyPipe, DatePipe } from '@angular/common'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { BillingFrequency } from '@appShared/services/lookup/[CodeGen]'
import { ISubscriptionConversionOption } from '@appShared/interfaces/[Model-based]'
import { CommonService, ToastrType } from '@appShared/services'
import { AccountDetailStore } from '../account-detail/account-detail.store'
import { AccountCardComponent } from '../account-card/account-card.component'

@Component({
   selector: 'app-change-plan-modal',
   templateUrl: './change-plan-modal.component.html',
   styleUrls: ['./change-plan-modal.component.less'],
   imports: [CurrencyPipe, DatePipe, AccountCardComponent],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangePlanModalComponent {
   public readonly activeModal = inject(NgbActiveModal)
   private readonly _store = inject(AccountDetailStore)
   private readonly _common = inject(CommonService)

   /* expose lookup to the template */
   public readonly BillingFrequency = BillingFrequency

   /* plan-change options (from the store) */
   public readonly actionableSubscription = this._store.actionableSubscription
   public readonly conversionOptions = this._store.subscriptionConversionOptions
   public readonly isLoading = this._store.accountIsLoading
   public readonly errorMessage = this._store.accountErrorMessage
   public readonly isSubmitting = this._store.planChangeIsSubmitting

   /** Set when the plan-change submit fails; shown inline in the footer. */
   public readonly submitErrorMessage = signal<string | null>(null)

   /** The plan the rep has clicked, pending an inline confirm in the footer. */
   public readonly selectedOption = signal<ISubscriptionConversionOption | null>(null)

   /** When the selected plan takes effect: the current plan's expiration, else the next payment date (if a different plan),
       deferred to the end of a pause. */
   public readonly selectedOptionEffective = computed(() => {
      const option = this.selectedOption()
      const subscription = this.actionableSubscription()
      if (!option || !subscription) {
         return null
      }
      const effective = subscription.product?.autoRenewalExpirationInMonths != null
         ? subscription.expires
         : (option.productSourceId === subscription.product?.sourceId ? null : subscription.nextPaymentDue)
      const pausedUntil = subscription.pausedUntil
      return effective && pausedUntil && new Date(pausedUntil) > new Date(effective) ? pausedUntil : effective
   })

   /** Guards the one-time pre-selection of the planned (next-renewal) option. */
   private readonly _selectionSeeded = signal<boolean>(false)

   constructor() {
      /* Pre-select the planned (next-renewal) option once the options load,
         so the footer confirm is primed to it by default. Runs once. */
      effect(() => {
         if (this._selectionSeeded()) {
            return
         }
         const planned = this.conversionOptions().find(option => option.isPlanned)
         if (planned) {
            this.selectedOption.set(planned)
            this._selectionSeeded.set(true)
         }
      })
   }

   /** Clicking a plan card selects it for confirmation. */
   selectPlan(option: ISubscriptionConversionOption): void {
      this.selectedOption.set(option)
   }

   /** Footer "Confirm" — applies the plan change, then closes on success. */
   async confirmChangePlan(): Promise<void> {
      const option = this.selectedOption()
      if (!option) {
         return
      }

      // Submit BEFORE closing — only close on success; keep the modal open on failure.
      this.submitErrorMessage.set(null)
      const result = await this._store.setPlannedConversionOption(option)
      if (result.ok) {
         this._common.messageUser('<div class="text-center">Plan Updated</div>')
         this.activeModal.close(option)
      } else {
         const message = result.error ?? 'Unable to change the plan. Please try again.'
         this.submitErrorMessage.set(message)
         this._common.messageUser(message, 'Plan Change Failed', ToastrType.error)
      }
   }

   cancel(): void {
      this.activeModal.dismiss('cancel')
   }
}
